/**
 * NavigateWidget - Search widget for people, communities, and tags
 * Reusable component for both desktop and mobile layouts
 */

import React, { useState, useEffect } from 'react';
import { Search, Hash } from 'lucide-react';
import { PixelatedTurtle } from '../../../shared/theme/design-constants';
import type { ThemeTokens } from '../../../shared/theme/tokens';

import { fetchWithAuth } from '../../../lib/fetchWithAuth';
interface NavigateWidgetProps {
  theme: ThemeTokens;
  className?: string;
  style?: React.CSSProperties;
  /** Hide the header/title */
  showHeader?: boolean;
  /** Additional className for the header */
  headerClassName?: string;
  /** Additional className for the content area */
  contentClassName?: string;
  /** Compact mode - reduces padding and spacing */
  compact?: boolean;
  /** Custom placeholder text for search input */
  placeholder?: string;
  /** Navigation callback - if not provided, uses window.location.href */
  onNavigate?: (path: string) => void;
}

interface SearchPerson {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  followers: number;
}

interface SearchCommunity {
  id: string;
  name: string;
  nameSlug?: string;
  avatar?: string;
  members: number;
}

interface SearchTag {
  tag: string;
  count: number;
}

export const NavigateWidget: React.FC<NavigateWidgetProps> = ({
  theme,
  className = '',
  style = {},
  showHeader = true,
  headerClassName = '',
  contentClassName = '',
  compact = false,
  placeholder = 'Search...',
  onNavigate,
}) => {
  // Default navigation fallback
  const navigate = onNavigate || ((path: string) => {
    window.location.href = path;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'people' | 'communities' | 'tags'>('people');
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [searchPeople, setSearchPeople] = useState<SearchPerson[]>([]);
  const [searchCommunities, setSearchCommunities] = useState<SearchCommunity[]>([]);
  const [searchTags, setSearchTags] = useState<SearchTag[]>([]);

  // Debounced search
  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery.trim()) {
        setSearchPeople([]);
        setSearchCommunities([]);
        setSearchTags([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        if (searchType === 'people') {
          const response = await fetchWithAuth(`/api/search/people?q=${encodeURIComponent(searchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSearchPeople(data.people || []);
          }
        } else if (searchType === 'communities') {
          const response = await fetchWithAuth(`/api/search/communities?q=${encodeURIComponent(searchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSearchCommunities(data.communities || []);
          }
        } else if (searchType === 'tags') {
          const response = await fetchWithAuth(`/api/search/tags?q=${encodeURIComponent(searchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSearchTags(data.tags || []);
            setShowResults(true);
          }
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchType]);

  const handleNavigateToPerson = (person: SearchPerson) => {
    const path = `/user/${person.username}`;
    navigate(path);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleNavigateToCommunity = (community: SearchCommunity) => {
    const path = `/community/${community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-')}`;
    navigate(path);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleNavigateToTag = (tag: string) => {
    const path = `/tags/${tag}`;
    navigate(path);
    setShowResults(false);
    setSearchQuery('');
  };

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{
        backgroundColor: theme.cardBg || 'rgba(255, 255, 255, 0.05)',
        borderColor: `${theme.accentColor}40`,
        border: '1px solid',
        ...style,
      }}
    >
      {showHeader && (
        <h3
          className={`font-bold text-center border-b ${compact ? 'py-2' : 'py-3'} ${headerClassName}`}
          style={{
            color: theme.textColor,
            borderColor: `${theme.accentColor}40`,
          }}
        >
          <Search className="inline-block w-4 h-4 mr-2" />
          Navigate
        </h3>
      )}

      <div className={`${compact ? 'p-2' : 'p-4'} ${contentClassName}`}>
        {/* Search Input */}
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchQuery.trim()) {
              setShowResults(true);
            }
          }}
          onFocus={() => {
            if (searchQuery.trim()) {
              setShowResults(true);
            }
          }}
          className={`w-full rounded-lg outline-none transition-all ${compact ? 'mb-2 px-2 py-1 text-sm' : 'mb-3 px-3 py-2'}`}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: theme.textColor,
            border: 'none',
          }}
        />

        {/* Search Type Selector */}
        <div
          className="flex gap-0 rounded-lg overflow-hidden"
          style={{
            border: `1px solid ${theme.accentColor}40`,
          }}
        >
          <button
            onClick={() => setSearchType('people')}
            className={`flex-1 text-sm font-semibold transition-all ${compact ? 'py-1' : 'py-2'}`}
            style={{
              backgroundColor: searchType === 'people' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
              color: theme.textColor,
            }}
          >
            People
          </button>
          <button
            onClick={() => setSearchType('communities')}
            className={`flex-1 text-sm font-semibold transition-all ${compact ? 'py-1' : 'py-2'}`}
            style={{
              backgroundColor: searchType === 'communities' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
              color: theme.textColor,
              borderLeft: `1px solid ${theme.accentColor}40`,
              borderRight: `1px solid ${theme.accentColor}40`,
            }}
          >
            Communities
          </button>
          <button
            onClick={() => setSearchType('tags')}
            className={`flex-1 text-sm font-semibold transition-all ${compact ? 'py-1' : 'py-2'}`}
            style={{
              backgroundColor: searchType === 'tags' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
              color: theme.textColor,
            }}
          >
            Tags
          </button>
        </div>

        {/* Search Results Dropdown */}
        {showResults && searchQuery.trim() && (
          <div
            className="mt-3 rounded-lg border overflow-hidden"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: `${theme.accentColor}40`,
              maxHeight: '400px',
            }}
          >
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              {/* People Results */}
              {searchType === 'people' && (
                <div className="p-3 space-y-2">
                  {isSearching ? (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      Searching...
                    </div>
                  ) : searchPeople.length > 0 ? (
                    searchPeople.map((person) => (
                      <div
                        key={person.id}
                        onClick={() => handleNavigateToPerson(person)}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        {person.avatar ? (
                          <img
                            src={person.avatar}
                            alt={person.displayName}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `linear-gradient(to bottom right, ${theme.accentColor}, ${theme.accentColor}dd)`,
                            }}
                          >
                            <PixelatedTurtle size={20} color={theme.accentColor} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate" style={{ color: theme.textColor }}>
                            {person.displayName}
                          </div>
                          <div className="text-xs truncate" style={{ color: theme.textColor + '99' }}>
                            @{person.username}
                          </div>
                        </div>
                        <div className="text-xs whitespace-nowrap" style={{ color: theme.textColor + '66' }}>
                          {person.followers} followers
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      No people found
                    </div>
                  )}
                </div>
              )}

              {/* Communities Results */}
              {searchType === 'communities' && (
                <div className="p-3 space-y-2">
                  {isSearching ? (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      Searching...
                    </div>
                  ) : searchCommunities.length > 0 ? (
                    searchCommunities.map((comm) => (
                      <div
                        key={comm.id}
                        onClick={() => handleNavigateToCommunity(comm)}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        {comm.avatar ? (
                          <img
                            src={comm.avatar}
                            alt={comm.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `linear-gradient(to bottom right, ${theme.accentColor}, ${theme.accentColor}dd)`,
                            }}
                          >
                            <Hash className="w-5 h-5" style={{ color: theme.textColor }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate" style={{ color: theme.textColor }}>
                            {comm.name}
                          </div>
                          <div className="text-xs truncate" style={{ color: theme.textColor + '99' }}>
                            {comm.members} members
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      No communities found
                    </div>
                  )}
                </div>
              )}

              {/* Tags Results */}
              {searchType === 'tags' && (
                <div className="p-3 space-y-2">
                  {isSearching ? (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      Searching...
                    </div>
                  ) : searchTags.length > 0 ? (
                    searchTags.map((tagData) => (
                      <div
                        key={tagData.tag}
                        onClick={() => handleNavigateToTag(tagData.tag)}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: theme.accentColor + '30',
                            border: `1px solid ${theme.accentColor}`,
                          }}
                        >
                          <Hash size={20} style={{ color: theme.textColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate" style={{ color: theme.textColor }}>
                            #{tagData.tag}
                          </div>
                          <div className="text-xs truncate" style={{ color: theme.textColor + '99' }}>
                            {tagData.count} posts
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
                      No tags found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
